import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * Editable inputs carry a persistent white box with a grey border, matching
 * Freeze v1's `.freeze__editable-cell`. v2 originally used a transparent
 * border that only appeared on hover, which made it impossible to tell at a
 * glance which cells accepted input.
 *
 * Disabled inputs drop the box so locked/read-only cells stay visually quiet —
 * the box means "you can type here right now".
 */
const BOXED_INPUT =
  'h-6 rounded border border-gray-300 bg-white px-1 hover:border-gray-400 focus:border-blue-400 focus:outline-none disabled:cursor-not-allowed disabled:border-transparent disabled:bg-transparent disabled:hover:border-transparent';

export const NUM_INPUT = `${BOXED_INPUT} w-full text-right`;
export const TEXT_INPUT = `${BOXED_INPUT} w-16`;
export const SELECT_INPUT = `${BOXED_INPUT} w-full px-0.5`;

/**
 * Moves focus to the same column of the next row that has an enabled input.
 *
 * ag-grid gave us this for free (see `useGridNav`), but the raw-table grids
 * render plain `<input>`s, so Enter only blurred and dropped the tech out of
 * the column entirely — they had to click every cell when typing a run of
 * stages/grades (WI 37941 #13). Rows whose cell is empty or disabled are
 * skipped rather than swallowing the keystroke.
 */
function focusNextRowCell(el: HTMLElement) {
  const td = el.closest('td');
  const tr = td?.parentElement;
  if (!td || !tr) return;

  const index = Array.prototype.indexOf.call(tr.children, td);
  let next = tr.nextElementSibling;
  while (next) {
    const cell = next.children[index];
    const input = cell?.querySelector<HTMLInputElement>(
      'input:not([disabled]):not([type="checkbox"])',
    );
    if (input) {
      input.focus();
      input.select();
      return;
    }
    next = next.nextElementSibling;
  }
}

export type EditableCellProps = {
  value: string | number | null;
  type: 'number' | 'text';
  className?: string;
  disabled?: boolean;
  /** Return `false` to reject the edit; the cell reverts to its previous value. */
  onCommit: (value: string) => boolean | void;
};

/**
 * An uncontrolled-feeling input that commits on blur or Enter.
 *
 * Edits are held locally so a slow save never fights the user's keystrokes,
 * and a rejected commit restores the prior value rather than leaving invalid
 * text in the cell.
 */
export function EditableCell({
  value,
  type,
  className,
  disabled,
  onCommit,
}: EditableCellProps) {
  const initial = value == null ? '' : String(value);
  const [val, setVal] = useState(initial);
  /**
   * What the cell has already handed to `onCommit`. Enter commits and then
   * moves focus, which fires `blur` on the way out — without this the same
   * edit would be saved twice.
   */
  const committed = useRef(initial);

  useEffect(() => {
    committed.current = initial;
    setVal(initial);
  }, [initial]);

  const commit = () => {
    if (val === committed.current) return true;
    const accepted = onCommit(val);
    if (accepted === false) {
      setVal(committed.current);
      return false;
    }
    committed.current = val;
    return true;
  };

  return (
    <input
      type={type}
      inputMode={type === 'number' ? 'numeric' : undefined}
      /* Inputs clip without an ellipsis, so a value wider than the column
         disappears with no visual cue (WI 37941 #4). Hover reveals the rest. */
      title={initial || undefined}
      className={cn(type === 'number' ? NUM_INPUT : TEXT_INPUT, className)}
      value={val}
      disabled={disabled}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        // Enter inside a grid means "next row", not "submit the page".
        e.preventDefault();
        if (commit()) focusNextRowCell(e.currentTarget);
      }}
    />
  );
}
